import { Card } from '@citizen-walk/shared-ui';
import { useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { DistrictAnalysisDisplayPanel } from './DistrictAnalysisDisplayPanel';
import { DistrictColorRulePanel } from './DistrictColorRulePanel';
import { MapMemoModerationPanel } from './MapMemoModerationPanel';
import { DistrictMapStylePanel } from './DistrictMapStylePanel';
import { DistrictPmtilesPanel } from './DistrictPmtilesPanel';
import { DistrictPostAreaPanel } from './DistrictPostAreaPanel';
import { DistrictSubPageHeader } from './DistrictSubPageHeader';
import { useDistrict } from './useDistricts';

/**
 * Sprint BG-Menu (第 3 段、DistrictDetailPage 分割): 分析画面の見た目設定ページ。
 *
 * 旧 DistrictDetailPage 内に同居していた以下 panel を `/districts/:id/display` に集約:
 *   - DistrictColorRulePanel (旧 v1 完全踏襲版 colorRule)
 *   - DistrictMapStylePanel (既定マップスタイル)
 *   - DistrictPostAreaPanel (投稿エリア)
 *   - DistrictPmtilesPanel (PMTiles 生成)
 */
export function DistrictDisplayPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const state = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();

  const isMaster = authState.role === 'master';
  // 地図マーカーの色/アイコン (ColorRule = 地区 doc の mapDisplay) は地区管理者も編集可
  // (firestore.rules で mapDisplay のみ field-scoped 許可)。担当地区の doc はそもそも
  // isDistrictAdmin しか read できず、本ページに到達できる = 編集権限ありと判断してよい。
  const canEditColorRule = isMaster || authState.role === 'district';
  const uid = authState.user?.uid ?? '';

  if (state.status === 'loading') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">読込中…</p>
      </Card>
    );
  }
  if (state.status === 'not-found') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-700">
          地区が見つかりません (id: <code>{districtId}</code>)。
        </p>
      </Card>
    );
  }
  if (state.status === 'error') {
    return (
      <Card padding="md" className="border-danger-600">
        <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
      </Card>
    );
  }

  return (
    <>
      <DistrictSubPageHeader
        districtId={state.district.id}
        districtName={state.district.name}
        activeTo={`/districts/${state.district.id}/display`}
      />
      <div className="grid grid-cols-1 gap-4">
        <Card padding="md">
          <h2 className="text-base font-bold text-slate-900 mb-1">地図・表示の設定</h2>
          <p className="text-xs text-slate-600">
            地図とマーカーの色・地図スタイル・投稿できるエリア・オフライン地図（ダウンロード用データの生成）・分析画面の表示設定・手書きメモのモデレーションを、ここでまとめて設定します。
          </p>
        </Card>

        <DistrictAnalysisDisplayPanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictPostAreaPanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictColorRulePanel district={state.district} uid={uid} canEdit={canEditColorRule} />
        {/* docs/24 P5b: 手書きメモのモデレーション (memoEnabled トグルは ColorRulePanel 内)。
            canEdit は ColorRule と同条件 (master / 担当地区管理者)。rules も isDistrictAdmin を許可。 */}
        <MapMemoModerationPanel
          districtId={state.district.id}
          uid={uid}
          canEdit={canEditColorRule}
        />
        <DistrictMapStylePanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictPmtilesPanel district={state.district} />
      </div>
    </>
  );
}
